import React from 'react';

export const DEFAULT_SCROLL_THRESHOLD = 150;

export type AutoScrollBehavior = 'smooth' | 'instant';

export interface UseAutoScrollOptions {
  /**
   * Ref to the scrollable container element
   */
  containerRef: React.RefObject<HTMLElement>;
  /**
   * Whether auto-scrolling is enabled (e.g., during streaming)
   */
  enabled?: boolean;
  /**
   * Distance from bottom (in pixels) to be considered "near bottom". Default is 150.
   */
  threshold?: number;
  /**
   * Increment this value to force an instant scroll to bottom (e.g., when sending a message)
   */
  forceScrollTrigger?: number;
  /**
   * Triggers smooth auto-scroll when value changes and enabled=true (e.g., messages.length during streaming)
   */
  scrollTrigger?: number;
}

export interface UseAutoScrollReturn {
  /**
   * Manually scroll to bottom with specified behavior
   */
  scrollToBottom: (behavior?: AutoScrollBehavior) => void;
}

/**
 * Hook for managing auto-scroll behavior in chat-like interfaces.
 *
 * Features:
 * - Tracks whether user is near bottom of scroll container
 * - Auto-scrolls smoothly when enabled and user is near bottom
 * - Supports force-scroll via trigger counter
 * - Handles layout shifts with multiple scroll attempts
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { scrollToBottom } = useAutoScroll({
 *   containerRef,
 *   enabled: isStreaming,
 *   forceScrollTrigger: messageCount,
 * });
 * ```
 */
export function useAutoScroll({
  containerRef,
  enabled = false,
  threshold = 150,
  forceScrollTrigger,
  scrollTrigger,
}: UseAutoScrollOptions): UseAutoScrollReturn {
  const isNearBottomRef = React.useRef(true);
  const cleanupRef = React.useRef<(() => void) | null>(null);

  // Scroll compensation: Track height changes to preserve user's reading position.
  // Initialized lazily at mount (see effect below) so prevHeightRef always has a real
  // baseline, even when scrollTrigger never changes.
  const prevHeightRef = React.useRef<number>(0);
  const prevEnabledRef = React.useRef<boolean>(false);
  const prevForceScrollTriggerRef = React.useRef<number | undefined>(forceScrollTrigger);

  // Scroll to bottom helper
  const scrollToBottom = React.useCallback(
    (behavior: AutoScrollBehavior = 'smooth') => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      // Cancel any pending cleanup
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      if (behavior === 'instant') {
        // Instant scroll with multiple attempts to handle layout shifts
        let rafId: number | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        const performScroll = () => {
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        };

        performScroll();
        rafId = requestAnimationFrame(() => {
          performScroll();
          // Additional attempt to catch slow layout shifts
          timeoutId = setTimeout(performScroll, 100);
        });

        // Store cleanup function
        cleanupRef.current = () => {
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
          }
          if (timeoutId !== null) {
            clearTimeout(timeoutId);
          }
        };
      } else {
        container.scrollTo({ top: container.scrollHeight, behavior });
      }
    },
    [containerRef],
  );

  // Seed prevHeightRef with the real scrollHeight at mount so the compensation effect
  // always has a valid baseline, even when scrollTrigger is never provided.
  React.useEffect(() => {
    const container = containerRef.current;
    if (container) {
      prevHeightRef.current = container.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track scroll position to determine if user is near bottom
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      isNearBottomRef.current = distanceFromBottom <= threshold;
    };

    // Initial check
    handleScroll();

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef, threshold]);

  // Force instant scroll to bottom when trigger changes (not on initial mount)
  React.useEffect(() => {
    const prevTrigger = prevForceScrollTriggerRef.current;

    if (forceScrollTrigger === undefined || forceScrollTrigger === prevTrigger) {
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      // Don't advance prevRef — retry when container mounts and effect re-runs
      return undefined;
    }

    prevForceScrollTriggerRef.current = forceScrollTrigger;

    // Mark as near bottom and force scroll
    isNearBottomRef.current = true;
    scrollToBottom('instant');

    return undefined;
  }, [containerRef, forceScrollTrigger, scrollToBottom]);

  // Auto-scroll smoothly when content changes during streaming
  React.useEffect(() => {
    if (!enabled || scrollTrigger === undefined) {
      return undefined;
    }

    if (isNearBottomRef.current) {
      scrollToBottom('smooth');
    }

    return undefined;
  }, [enabled, scrollTrigger, scrollToBottom]);

  /**
   * Scroll Position Compensation
   *
   * Problem: When streaming finishes, content transforms cause height changes:
   * - Collapsible blocks auto-collapse (thinking/analysis sections)
   * - Typewriter text → formatted markdown rendering
   * - Risk score evidence sections appear/disappear
   *
   * If user scrolled up to read earlier messages, these height changes would
   * cause jarring scroll jumps, making them lose their place.
   *
   * Solution: When stream finishes, measure height delta and adjust scrollTop
   * to compensate, preserving the user's reading position.
   *
   * Note: If user is at bottom, auto-scroll handles it (no compensation needed).
   *
   * ORDERING: Must be defined BEFORE the height-tracking effect below.
   * When `enabled` and `scrollTrigger` change in the same render, React runs
   * effects in definition order. This effect reads `prevHeightRef` (last streaming
   * height) before the tracking effect overwrites it with the post-collapse value.
   */
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const wasEnabled = prevEnabledRef.current;
    prevEnabledRef.current = enabled;

    // Only compensate on the streaming → finished transition
    const streamJustFinished = wasEnabled && !enabled;
    if (!streamJustFinished) {
      return undefined;
    }

    const currentHeight = container.scrollHeight;
    const heightDelta = currentHeight - prevHeightRef.current;

    if (!isNearBottomRef.current && heightDelta !== 0) {
      container.scrollTop = Math.max(0, container.scrollTop + heightDelta);
    }

    return undefined;
  }, [containerRef, enabled]);

  // Track the latest scrollHeight on each content update so the compensation
  // effect above has an accurate baseline when the stream finishes.
  // ORDERING: Must be defined AFTER the compensation effect (see note above).
  React.useEffect(() => {
    const container = containerRef.current;
    if (container) {
      prevHeightRef.current = container.scrollHeight;
    }
  }, [containerRef, scrollTrigger]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  return {
    scrollToBottom,
  };
}
